/**
 * 台帳一覧に出す、利用者向けの説明（台帳ごとに1〜2文: 何を記録するか／いつ見直すか）。
 *
 * DB の RegisterDefinition.description は設計メモ（評価基準・原典・TODO 番号との対応）で、
 * 利用者に見せる文ではない。ここに無い台帳はその説明を出さない（RegisterList.tsx）。
 * 台帳名は制度用語なので訳さない（英語画面でも日本語名のまま）。説明は訳す。
 */
export const ja: Record<string, string> = {
  "regdesc.CONTACT": "社内・子会社・委託先・外部機関の緊急連絡先です。年に1回以上、実際に連絡がつくか確かめます。",
  "regdesc.EXTERNAL_SYSTEM": "社外が管理していて当社が使っているシステムやクラウドサービス（Microsoft 365、Google Workspace、AWS など）です。",
  "regdesc.DEVICE": "会社のパソコン・スマートフォン・シンクライアントです。利用者・OS・マルウェア対策から廃棄までを記録します。",
  "regdesc.SOFTWARE": "導入しているソフトウェアと、ライセンスの数・サポート期限です。",
  "regdesc.NETWORK_SEGMENT": "社内ネットワークの区分（セグメント）と、その所在地・用途です。",
  "regdesc.NETWORK_DEVICE": "ファイアウォール・ルーター・スイッチなどの機器と、保守の契約・期限です。",
  "regdesc.CRITICAL_INFO": "重要な機密情報ごとの管理者・保管場所・保管期限・開示先です。パスワードなどの値そのものは書きません。",
  "regdesc.SYSTEM_ADMIN": "システムごとに、当社側の責任者と委託先側の管理者を組にして記録します。",
  "regdesc.ADMIN_ID": "管理者権限を持つ ID と、その持ち主・用途、付与と削除の記録です。",
  "regdesc.USER_ACCESS": "社員などの ID と、どのシステムでどんな権限を持っているかです。退職や異動のときに見直します。",
  "regdesc.SHARED_ID": "複数人で使う ID と、実際に使う人です。共有の ID が無ければ「該当なし」にします。",
  "regdesc.ROOM_ACCESS": "立ち入りを制限している部屋に入れる人と、その期間です。該当する部屋が無ければ「該当なし」にします。",
  "regdesc.FW_RULE": "ファイアウォールの通信ルールです。変更の履歴から、過去のある日のルールを再現できます。",
  "regdesc.ALLOWED_SOFTWARE": "使ってよいソフトウェアと、使ってはいけないソフトウェア（理由つき）です。",
  "regdesc.SERVER_ASSET": "サーバー・仮想サーバー・ハイパーバイザーです。自社で持っていなければ、委託先が提供しているものを記録します。",
  "regdesc.PATCH": "セキュリティ更新（パッチ）の適用状況です。重大なものはリリースから14日以内に適用し、できないときは代わりの対策を書きます。",
  "regdesc.MALWARE_PROTECTION": "パソコンごとのマルウェア対策ソフトと、スキャン・定義更新の状況です。",
  "regdesc.MFA_METHOD": "多要素認証を使っているシステムと、その方式です。",
  "regdesc.BACKUP_TARGET": "バックアップを取る対象・頻度・保管期間です。規程に書いた値と同じものを使います。",
  "regdesc.RECOVERY_PLAN": "サービスが止まったときに戻す手順と、目標とする復旧時間です。",
  "regdesc.AGREEMENT": "委託先・子会社と結んだ覚書や契約と、その更新期限です。",
  "regdesc.INCIDENT": "セキュリティ事故の報告と、発見から最終報告までの対応の記録です。",
  "regdesc.TEMP_DATA": "障害の調査などで本番環境から一時的に取り出したデータの、許可と削除の記録です。",
  "regdesc.PROD_ACCESS_LOG": "本番環境に誰が・いつ・何のために入ったかの記録です。",
  "regdesc.DISPOSAL": "機器を廃棄するときに、データを消したことの記録です。",
  "regdesc.VENDOR_EVIDENCE": "委託先から受け取った証跡（設定の記録など）と、受け取った日・対象の期間です。",
  "regdesc.WORK_REPORT": "ネットワーク機器の作業のたびに受け取る作業報告書の記録です。",

  "reggroup.people": "体制と連絡先",
  "reggroup.endpoint": "パソコン・ソフトウェア",
  "reggroup.network": "ネットワーク",
  "reggroup.identity": "ID と権限",
  "reggroup.information": "情報と外部サービス",
  "reggroup.continuity": "バックアップと復旧",
  "reggroup.records": "記録",
  "reggroup.other": "その他の台帳",

  "reg.list.intro": "台帳を開くと、内容の確認・追加・修正と、変更の履歴の確認、CSV からの取り込みができます。",
  "reg.list.emptyBody": "台帳がまだ用意されていません。セキュリティ担当部署長に連絡してください。",
  "reg.imported": "自動で取り込み",
  "reg.importedHint": "外部のシステムや CSV から内容を取り込める台帳です",
  "reg.criteriaSummary": "関係する評価基準: {first}",
  "reg.criteriaSummaryMore": "関係する評価基準: {first} ほか {n} 件",
};

export const en: Record<string, string> = {
  "regdesc.CONTACT": "Emergency contacts inside the company, at subsidiaries, contractors and outside bodies. Check at least once a year that each one can be reached.",
  "regdesc.EXTERNAL_SYSTEM": "Systems and cloud services run by other companies that we use, such as Microsoft 365, Google Workspace and AWS.",
  "regdesc.DEVICE": "Company PCs, smartphones and thin clients: who uses them, OS and malware protection, through to disposal.",
  "regdesc.SOFTWARE": "Installed software, with licence counts and support end dates.",
  "regdesc.NETWORK_SEGMENT": "Network segments, with their location and purpose.",
  "regdesc.NETWORK_DEVICE": "Firewalls, routers, switches and similar devices, with maintenance contracts and end dates.",
  "regdesc.CRITICAL_INFO": "For each piece of critical confidential information: owner, where it is kept, for how long, and who it is shared with. Never enter passwords or other secret values.",
  "regdesc.SYSTEM_ADMIN": "For each system, the person responsible on our side paired with the administrator at the contractor.",
  "regdesc.ADMIN_ID": "Accounts with administrator rights: who holds them, what for, and when they were granted and removed.",
  "regdesc.USER_ACCESS": "User accounts and what access each one has in which system. Review when someone leaves or changes roles.",
  "regdesc.SHARED_ID": "Accounts used by several people, and who actually uses each one. Mark as not applicable if there are none.",
  "regdesc.ROOM_ACCESS": "Who may enter restricted rooms, and for how long. Mark as not applicable if there are none.",
  "regdesc.FW_RULE": "Firewall rules. The change history lets you see the rules as they were on any past date.",
  "regdesc.ALLOWED_SOFTWARE": "Software that may be used, and software that may not (with the reason).",
  "regdesc.SERVER_ASSET": "Servers, virtual servers and hypervisors. If we own none, record the ones contractors provide for us.",
  "regdesc.PATCH": "Security updates (patches). Apply critical ones within 14 days of release; if you can't, record what you did instead.",
  "regdesc.MALWARE_PROTECTION": "Malware protection on each computer, with scan and definition-update status.",
  "regdesc.MFA_METHOD": "Systems that use multi-factor authentication, and the method each one uses.",
  "regdesc.BACKUP_TARGET": "What is backed up, how often, and how long it is kept. Uses the same values as the backup policy.",
  "regdesc.RECOVERY_PLAN": "How to bring each service back if it stops, and the target recovery time.",
  "regdesc.AGREEMENT": "Memoranda and contracts with contractors and subsidiaries, with their renewal dates.",
  "regdesc.INCIDENT": "Security incident reports and the response from discovery to final report.",
  "regdesc.TEMP_DATA": "Permission for, and deletion of, data taken out of production temporarily, for example to investigate a fault.",
  "regdesc.PROD_ACCESS_LOG": "Who accessed production, when, and why.",
  "regdesc.DISPOSAL": "Proof that data was erased when equipment was disposed of.",
  "regdesc.VENDOR_EVIDENCE": "Evidence received from contractors, such as configuration records, with the date received and the period it covers.",
  "regdesc.WORK_REPORT": "Work reports received for each job done on network equipment.",

  "reggroup.people": "Organisation and contacts",
  "reggroup.endpoint": "Computers and software",
  "reggroup.network": "Network",
  "reggroup.identity": "Accounts and access",
  "reggroup.information": "Information and outside services",
  "reggroup.continuity": "Backup and recovery",
  "reggroup.records": "Records",
  "reggroup.other": "Other registers",

  "reg.list.intro": "Open a register to view, add and edit entries, see their change history, or import from a CSV file.",
  "reg.list.emptyBody": "No registers have been set up yet. Contact the head of the security department.",
  "reg.imported": "Auto-import",
  "reg.importedHint": "Entries in this register can be imported from another system or a CSV file",
  "reg.criteriaSummary": "Related criteria: {first}",
  "reg.criteriaSummaryMore": "Related criteria: {first} and {n} more",
};
